import * as React from 'react';
import { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import {
    Box,
    Button,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    InputAdornment,
    IconButton,
    Collapse,
    Divider,
    MenuItem,
} from '@mui/material';
import axios from 'axios';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import TableLoader from '../GlobalComponents/TableLoader';
import AddButton from '../GlobalComponents/AddButton';

interface EqualizerTenant {
    id: number;
    tenant: string;
}

interface EqualizerTenantCoinConfig {
    id?: number;
    equalizerTenantId: number;
    coin: string;
    prefix: string;
}

function Row(props: { row: EqualizerTenant; onUpdate: () => void }) {
    const { row, onUpdate } = props;
    const [open, setOpen] = useState(false);
    const [configs, setConfigs] = useState<EqualizerTenantCoinConfig[]>([]);
    const [configModalOpen, setConfigModalOpen] = useState(false);
    const [newConfig, setNewConfig] = useState<EqualizerTenantCoinConfig>({
        equalizerTenantId: row.id,
        coin: '',
        prefix: '',
    });

    const fetchConfigs = () => {
        axios.get(`/api/machines/equalizer_tenant/${row.id}/coin_configs`, getAxiosConfig())
            .then(response => setConfigs(response.data))
            .catch(error => console.error("Error fetching configs:", error));
    };

    useEffect(() => {
        if (open) {
            fetchConfigs();
        }
    }, [open]);

    const handleSaveConfig = () => {
        if (!newConfig.coin || !newConfig.prefix) return;
        axios.post('/api/machines/equalizer_tenant/coin_config', newConfig, getAxiosConfig())
            .then(() => {
                fetchConfigs();
                setConfigModalOpen(false);
                setNewConfig({ equalizerTenantId: row.id, coin: '', prefix: '' });
            })
            .catch(error => console.error("Error saving config:", error));
    };

    const handleDeleteConfig = (id: number) => {
        if (!window.confirm("Are you sure you want to delete this configuration?")) return;
        axios.delete(`/api/machines/equalizer_tenant/coin_config/${id}`, getAxiosConfig())
            .then(() => fetchConfigs())
            .catch(error => console.error("Error deleting config:", error));
    };

    return (
        <React.Fragment>
            <TableRow hover>
                <TableCell>
                    <IconButton size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row.tenant}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, p: 2, bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1e1e1e' : '#fafafa', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 'bold' }}>
                                    Coin Configurations
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    variant="outlined"
                                    onClick={() => setConfigModalOpen(true)}
                                >
                                    Add Config
                                </Button>
                            </Box>
                            <Table size="small" aria-label="configs">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Coin</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Prefix</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {configs.map((config) => (
                                        <TableRow key={config.id}>
                                            <TableCell>{config.coin}</TableCell>
                                            <TableCell>{config.prefix}</TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" color="error" onClick={() => handleDeleteConfig(config.id!)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {configs.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                                                No coin configurations defined.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>

            <Dialog open={configModalOpen} onClose={() => setConfigModalOpen(false)}>
                <DialogTitle>Add Coin Configuration for {row.tenant}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minWidth: 300 }}>
                        <TextField
                            select
                            label="Coin"
                            value={newConfig.coin}
                            onChange={(e) => setNewConfig({ ...newConfig, coin: e.target.value })}
                            fullWidth
                        >
                            <MenuItem value="BTC">BTC</MenuItem>
                            <MenuItem value="KAS">KAS</MenuItem>
                            <MenuItem value="ALPH">ALPH</MenuItem>
                        </TextField>
                        <TextField
                            label="Prefix"
                            placeholder="e.g. msale."
                            value={newConfig.prefix}
                            onChange={(e) => setNewConfig({ ...newConfig, prefix: e.target.value })}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setConfigModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveConfig} variant="contained" disabled={!newConfig.coin || !newConfig.prefix}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

export default function EqualizerTenantList() {
    const [rows, setRows] = useState<EqualizerTenant[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [newTenantName, setNewTenantName] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState<string>('');

    useEffect(() => {
        fetchTenants();
    }, []);

    const fetchTenants = () => {
        setLoading(true);
        axios.get('/api/machines/equalizer_tenants', getAxiosConfig())
            .then(response => {
                setRows(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching equalizer tenants:", error);
                setError("Failed to fetch data.");
                setLoading(false);
            });
    };

    const handleOpenModal = () => setModalOpen(true);
    const handleCloseModal = () => {
        setModalOpen(false);
        setNewTenantName('');
    };

    const handleAddTenant = () => {
        if (!newTenantName.trim()) return;

        setLoading(true);
        axios.post('/api/machines/equalizer_tenant', { tenant: newTenantName }, getAxiosConfig())
            .then(() => {
                fetchTenants();
                handleCloseModal();
            })
            .catch(error => {
                console.error("Error saving equalizer tenant:", error);
                setError("Failed to save data.");
                setLoading(false);
            });
    };

    const filteredRows = rows.filter(row =>
        row.tenant.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" fontWeight="bold" color="primary">
                    Equalizer Organizations
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search organizations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: 300 }}
                />
            </Box>

            <Paper elevation={3} sx={{ padding: 0, marginTop: 2, overflow: 'hidden' }}>
                {loading && rows.length === 0 ? (
                    <TableLoader />
                ) : error ? (
                    <Box sx={{ p: 3 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <Table sx={{ minWidth: 650 }} aria-label="equalizer tenant table">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#333' : '#f5f5f5' }}>
                                <TableCell sx={{ width: 50 }} />
                                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Organization Name</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredRows.map((row) => (
                                <Row key={row.id} row={row} onUpdate={fetchTenants} />
                            ))}
                            {filteredRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                                        No organizations found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            <AddButton onClick={handleOpenModal} />

            <Dialog open={modalOpen} onClose={handleCloseModal}>
                <DialogTitle>Add New Organization</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Organization Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newTenantName}
                        onChange={(e) => setNewTenantName(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseModal}>Cancel</Button>
                    <Button
                        onClick={handleAddTenant}
                        variant="contained"
                        disabled={!newTenantName.trim() || loading}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
